import { useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import { useUnifiedBusinessEntityContext } from '#client/contexts/UnifiedBusinessEntityProvider';
import { GET_USER_THREADS } from '#client/queries/getUserThreads';

import useUserMode from './useUserMode';

/**
 * Hook to fetch threads for the current user
 * @param {object} options - Query options
 * @param {number} options.pageSize - Number of threads per page (default: 20)
 * @param {string} options.cursor - Cursor for pagination
 * @param {string} options.sort - Sort order (default: '-createdDatetime')
 * @returns {object} Hook state with threads data, loading, and error
 */
export function useUserThreads({ pageSize = 50, cursor = null, sort = '-createdDatetime' } = {}) {
  // Get userUuid directly from auth token instead of waiting for GraphQL identity query
  // This bypasses the IdentityProvider's CurrentAccount query which may fail for some account types
  const { authenticated, userUuid } = useAuthentication();
  const { businessEntityUuid } = useUnifiedBusinessEntityContext();

  // Get current user mode to filter threads by mode and gate the query
  const userMode = useUserMode();

  // Memoize identity to prevent unnecessary query re-executions
  // Only create identity object when userUuid exists
  const identity = useMemo(
    () =>
      userUuid
        ? {
            userUuid,
            ...(businessEntityUuid && { businessEntityUuid })
          }
        : null,
    [userUuid, businessEntityUuid]
  );

  // Skip query when unauthenticated, missing identity, or anonymous mode (no thread history)
  const shouldSkip = !authenticated || !identity || userMode.isAnonymous;

  const { data, loading, errors, refetch } = useQuery(GET_USER_THREADS, {
    noThrow: true,
    skip: shouldSkip,
    variables: {
      identity,
      pageSize,
      cursor,
      sort,
      mode: userMode.mode
    }
  });

  // Suppress auth/session-expired errors silently — stale tokens cause 401s before
  // the client detects expiry. Show empty sidebar rather than flashing an error.
  const isAuthError = errors?.some(
    (e) =>
      e.message?.toLowerCase().includes('session') ||
      e.message?.toLowerCase().includes('unauthorized') ||
      e.extensions?.code === 'UNAUTHENTICATED'
  );
  useError(isAuthError ? null : errors);

  // Transform the backend thread data to match our frontend expectations
  const transformThread = (thread) => ({
    uuid: thread.uuid,
    userUuid: thread.userUuid,
    threadUuid: thread.threadUuid,
    createdAt: thread.createdDatetime,
    updatedAt: thread.updatedDatetime,
    lastMessageAt: thread.updatedDatetime, // Use updated time as last message time
    title: thread.title || null, // Use title from backend, null if not set
    messageCount: 0 // Not available from backend yet
  });

  const threadsData = data?.getUserThreads;
  const threads = threadsData?.threads?.map(transformThread) || [];

  return {
    threads,
    total: threadsData?.total || 0,
    hasNextPage: threadsData?.hasNextPage || false,
    nextCursor: threadsData?.nextCursor || null,
    loading,
    error: errors?.[0],
    refetch: () => {
      return refetch({ fetchPolicy: 'network-only' });
    }
  };
}
